import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';

export default function ChronicDiseaseManagement() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/chronic-disease-management#webpage`,
    url: `${siteUrl}/services/chronic-disease-management`,
    name: 'Chronic Disease Management Las Vegas | CHF, COPD & Diabetes',
    description: 'In-home chronic disease management in Las Vegas — comprehensive care for CHF, COPD, diabetes, hypertension, and other chronic conditions. Reduce hospitalizations with ACHC accredited care.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'InternalMedicine',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Chronic Disease Management', item: `${siteUrl}/services/chronic-disease-management` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Chronic Disease Management Las Vegas | CHF, COPD & Diabetes';
  }, []);

  const diseasePrograms = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'CHF Management Program at Home',
      description: 'Comprehensive congestive heart failure management program designed to prevent hospitalizations and improve quality of life in Las Vegas homes.',
      image: 'https://public.readdy.ai/ai/img_res/47c0fca9a97e95bdf026ef695b0b72ec.jpg',
      benefits: [
        'Daily weight and vital sign monitoring',
        'Medication management and compliance',
        'Dietary counseling for heart health',
        'Fluid restriction education and monitoring',
        'Activity tolerance assessment',
        'Early symptom recognition training',
        'Emergency response protocols',
        'Coordination with cardiologist'
      ],
      keyServices: 'Cardiac monitoring, medication management, lifestyle education',
      outcomes: 'Reduced hospitalizations, improved symptoms, better quality of life'
    },
    {
      icon: 'ri-lungs-line',
      title: 'COPD Care and Education at Home',
      description: 'Specialized chronic obstructive pulmonary disease management program focusing on breathing techniques and lifestyle modifications.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20respiratory%20therapist%20helping%20elderly%20patient%20with%20COPD%20breathing%20exercises%20in%20bright%20modern%20home%20bedroom%2C%20pulmonary%20rehabilitation%20at%20home%2C%20clean%20white%20interior%20with%20oxygen%20equipment%2C%20therapist%20teaching%20breathing%20techniques%20in%20residential%20setting&width=500&height=350&seq=copd-care-lv&orientation=landscape',
      benefits: [
        'Breathing technique instruction',
        'Oxygen therapy management',
        'Medication administration training',
        'Pulmonary rehabilitation exercises',
        'Energy conservation techniques',
        'Infection prevention education',
        'Smoking cessation support',
        'Emergency action plan development'
      ],
      keyServices: 'Respiratory therapy, oxygen management, pulmonary rehabilitation',
      outcomes: 'Improved breathing, reduced exacerbations, enhanced mobility'
    },
    {
      icon: 'ri-drop-line',
      title: 'Diabetes Management at Home',
      description: 'Comprehensive diabetes care program including blood sugar monitoring, medication management, and lifestyle education in Las Vegas homes.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20diabetes%20educator%20helping%20elderly%20patient%20with%20blood%20glucose%20monitoring%20in%20comfortable%20home%20kitchen%2C%20diabetes%20management%20at%20home%2C%20clean%20white%20interior%20with%20diabetic%20supplies%2C%20nurse%20teaching%20insulin%20administration%20in%20residential%20setting&width=500&height=350&seq=diabetes-management-lv&orientation=landscape',
      benefits: [
        'Blood glucose monitoring and tracking',
        'Insulin administration training',
        'Diabetic meal planning and nutrition',
        'Foot care and wound prevention',
        'Medication timing and compliance',
        'Hypoglycemia recognition and treatment',
        'Exercise planning and safety',
        'Complication prevention education'
      ],
      keyServices: 'Glucose monitoring, insulin management, nutritional counseling',
      outcomes: 'Better glucose control, reduced complications, improved self-care'
    },
    {
      icon: 'ri-pulse-line',
      title: 'Hypertension Monitoring at Home',
      description: 'Blood pressure management program with regular monitoring, medication optimization, and lifestyle modification support.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20nurse%20taking%20blood%20pressure%20of%20elderly%20patient%20in%20comfortable%20home%20living%20room%2C%20hypertension%20monitoring%20at%20home%2C%20clean%20white%20interior%20with%20blood%20pressure%20equipment%2C%20nurse%20checking%20vital%20signs%20in%20residential%20setting&width=500&height=350&seq=hypertension-monitoring-lv&orientation=landscape',
      benefits: [
        'Regular blood pressure monitoring',
        'Medication adherence support',
        'Dietary modification guidance',
        'Weight management counseling',
        'Stress reduction techniques',
        'Exercise program development',
        'Risk factor identification',
        'Physician communication and reporting'
      ],
      keyServices: 'BP monitoring, medication management, lifestyle counseling',
      outcomes: 'Controlled blood pressure, reduced cardiovascular risk, better health'
    }
  ];

  const managementSteps = [
    {
      step: '1',
      title: 'Comprehensive Assessment',
      description: 'Thorough evaluation of current health status, medications, and home environment.',
      icon: 'ri-stethoscope-line'
    },
    {
      step: '2',
      title: 'Personalized Care Plan',
      description: 'Development of individualized management plan based on specific condition and needs.',
      icon: 'ri-file-list-3-line'
    },
    {
      step: '3',
      title: 'Education and Training',
      description: 'Patient and family education on disease management, medications, and self-care.',
      icon: 'ri-book-open-line'
    },
    {
      step: '4',
      title: 'Regular Monitoring',
      description: 'Ongoing assessment of symptoms, vital signs, and treatment effectiveness.',
      icon: 'ri-heart-pulse-line'
    },
    {
      step: '5',
      title: 'Care Coordination',
      description: 'Communication with physicians and specialists to optimize treatment plans.',
      icon: 'ri-team-line'
    }
  ];

  const conditions = [
    'Congestive Heart Failure (CHF)',
    'Chronic Obstructive Pulmonary Disease (COPD)',
    'Diabetes Type 1 and Type 2',
    'Hypertension (High Blood Pressure)',
    'Chronic Kidney Disease',
    'Arthritis and Joint Conditions',
    'Osteoporosis and Bone Health',
    'Chronic Pain Conditions',
    'Stroke Recovery and Prevention',
    'Peripheral Artery Disease',
    'Atrial Fibrillation',
    'Chronic Wounds and Ulcers'
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-chronic-disease" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-6xl font-bold mb-6">Chronic Disease Management at Home</h1>
              <p className="text-xl text-white/90 leading-relaxed mb-8">
                Specialized <strong>chronic disease management programs</strong> for Las Vegas residents. 
                Professional nursing care and education for CHF, COPD, diabetes, and hypertension management 
                in the comfort of your Henderson, Summerlin, and North Las Vegas home.
              </p>
              <div className="bg-amber-900/30 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-amber-700/30">
                <h3 className="text-xl font-bold mb-3">Specialized Programs:</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-amber-100">
                  <span>• CHF management program</span>
                  <span>• COPD care and education</span>
                  <span>• Diabetes management at home</span>
                  <span>• Hypertension monitoring</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-amber-600 hover:bg-gray-50 shadow-2xl whitespace-nowrap">
                    Start Disease Management
                  </Button>
                </Link>
                <a href="tel:866-939-0330">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-amber-600 shadow-2xl whitespace-nowrap">
                    Call: 866-939-0330
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20team%20providing%20comprehensive%20chronic%20disease%20management%20in%20Las%20Vegas%20home%2C%20skilled%20nurses%20monitoring%20patient%20with%20multiple%20chronic%20conditions%20in%20comfortable%20residential%20setting%20with%20modern%20medical%20equipment%20and%20bright%20interior%20design&width=500&height=400&seq=chronic-disease-hero-lv&orientation=landscape"
                alt="Chronic Disease Management at Home Las Vegas - Professional Care"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Disease Management Programs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Chronic Disease Programs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized <strong>chronic disease management at home</strong> programs designed to improve 
              quality of life and prevent hospitalizations for Las Vegas residents.
            </p>
          </div>

          <div className="space-y-20">
            {diseasePrograms.map((program, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                      <i className={`${program.icon} text-white text-3xl`}></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{program.title}</h3>
                  </div>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    {program.description}
                  </p>
                  <div className="mb-8">
                    <h4 className="text-xl font-bold text-gray-900 mb-4">Program Benefits:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {program.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center bg-amber-50 rounded-lg p-3 border border-amber-200/50">
                          <i className="ri-check-line text-amber-600 mr-3 text-lg"></i>
                          <span className="text-gray-700 font-medium">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-r from-amber-50 to-stone-50 rounded-xl p-6 border border-amber-200">
                      <h5 className="font-bold text-gray-900 mb-2">Key Services</h5>
                      <p className="text-amber-700 font-semibold">{program.keyServices}</p>
                    </div>
                    <div className="bg-gradient-to-r from-amber-50 to-stone-50 rounded-xl p-6 border border-amber-200">
                      <h5 className="font-bold text-gray-900 mb-2">Expected Outcomes</h5>
                      <p className="text-amber-700 font-semibold">{program.outcomes}</p>
                    </div>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img
                      src={program.image}
                      alt={`${program.title} - Chronic Disease Management Las Vegas`}
                      className="rounded-2xl shadow-2xl object-cover w-full h-96"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Management Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Disease Management Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach to managing chronic conditions and improving health outcomes
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 rounded-full"></div>
            
            <div className="space-y-16">
              {managementSteps.map((step, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <div className="bg-white rounded-2xl p-8 shadow-xl border border-amber-200">
                      <div className={`flex items-center mb-4 ${index % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                        <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                          <i className={`${step.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-amber-700 rounded-full flex items-center justify-center shadow-2xl border-4 border-white">
                      <span className="text-white font-bold text-xl">{step.step}</span>
                    </div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Conditions We Manage */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Chronic Conditions We Manage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive management programs for a wide range of chronic health conditions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {conditions.map((condition, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-amber-200">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg flex items-center justify-center mr-4 shadow-md">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{condition}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Benefits of Home-Based Disease Management
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Why managing chronic conditions at home leads to better outcomes and quality of life
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-hospital-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Reduced Hospitalizations</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Proactive management and early intervention help prevent emergency situations and hospital readmissions.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-user-smile-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Improved Quality of Life</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Better symptom management and self-care education lead to enhanced daily functioning and well-being.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                <i className="ri-money-dollar-circle-line text-white text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cost-Effective Care</h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                Home-based management reduces healthcare costs while providing personalized, high-quality care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-80 h-80 bg-amber-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-600/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-8">
                Ready to Manage Your Chronic Condition at Home?
              </h2>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                Contact Celebrity Home Health today to learn how our specialized <strong>chronic disease management programs</strong> 
                can help you live better with your condition in the comfort of your Las Vegas home.
              </p>
              <div className="flex flex-col sm:flex-row gap-6">
                <Link to="/contact">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-amber-600 hover:bg-gray-50 shadow-2xl whitespace-nowrap">
                    Start Disease Management
                  </Button>
                </Link>
                <a href="tel:866-939-0330">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-amber-600 shadow-2xl whitespace-nowrap">
                    Call: 866-939-0330
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-3xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20elderly%20patient%20successfully%20managing%20chronic%20disease%20at%20home%20with%20professional%20healthcare%20team%20in%20Las%20Vegas%2C%20patient%20smiling%20with%20nurse%20monitoring%20health%20conditions%20in%20comfortable%20residential%20setting%2C%20successful%20chronic%20disease%20management%20outcomes&width=500&height=400&seq=cta-chronic-success-lv&orientation=landscape"
                  alt="Chronic Disease Management Success - Las Vegas Home Care"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
